
import { useState } from "react";
import { Task } from "../types/tasks";
import { useTaskFilters } from "./tasks/useTaskFilters";
import { useTaskActions } from "./tasks/useTaskActions";
import { useTaskChangeRequests } from "./tasks/useTaskChangeRequests";
import { initialTasks } from "../data/initialTasks";

export const useTasks = (userId?: string) => {
  const [tasks, setTasks] = useState<Task[]>(initialTasks);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [isTaskDetailOpen, setIsTaskDetailOpen] = useState(false);

  // Import hooks for different task functionalities
  const { filters, setFilters, viewMode, setViewMode, getFilteredTasks } = useTaskFilters();
  
  const { 
    handleTaskStatusChange, 
    handleViewTask, 
    handleTaskUpdate, 
    handleAddComment 
  } = useTaskActions(tasks, setTasks, selectedTask, setSelectedTask);
  
  const { 
    handleRequestChange, 
    handleChangeRequestResponse, 
    getPendingRequestsCount 
  } = useTaskChangeRequests(tasks, setTasks, selectedTask, setSelectedTask);

  // Wrapper for view task that also opens the dialog
  const viewTaskWithDialog = (id: string) => {
    const found = handleViewTask(id);
    if (found) {
      setIsTaskDetailOpen(true);
    }
  };

  // Wrapper for add comment that includes userId
  const addCommentWithUser = (taskId: string, content: string) => {
    handleAddComment(taskId, content, userId);
  };

  // Wrapper for request change that includes userId
  const requestChangeWithUser = (taskId: string, requestType: "reassign" | "extend", details: string) => {
    handleRequestChange(taskId, requestType, details, userId);
  };

  // Add a new task
  const addTask = (task: Task) => {
    setTasks(prevTasks => [task, ...prevTasks]);
  };

  // Get filtered tasks based on current filters
  const filteredTasks = getFilteredTasks(tasks, userId);
  
  // Get pending requests count for admin notifications
  const pendingRequestsCount = getPendingRequestsCount(tasks);

  return {
    tasks: filteredTasks,
    filters,
    setFilters,
    viewMode,
    setViewMode,
    selectedTask,
    isTaskDetailOpen,
    setIsTaskDetailOpen,
    handleTaskStatusChange,
    handleViewTask: viewTaskWithDialog,  // Renamed to avoid confusion
    handleTaskUpdate,
    handleAddComment: addCommentWithUser,
    handleRequestChange: requestChangeWithUser,
    handleChangeRequestResponse,
    pendingRequestsCount,
    addTask
  };
};
